import type { PhotoUploadItem } from './usePhotoUploadQueue'

type Listener = () => void

/**
 * Every queued photo across the app, so a panel closing mid-upload loses
 * neither the upload nor its retry row, and the page warns before unloading
 * while bytes are still moving.
 */
let items: PhotoUploadItem[] = []
const listeners = new Set<Listener>()
let guardingUnload = false

const inFlight = (item: PhotoUploadItem): boolean =>
  item.phase === 'uploading' || item.phase === 'attaching'

const warnBeforeUnload = (event: BeforeUnloadEvent) => {
  event.preventDefault()
}

function syncUnloadGuard() {
  const shouldGuard = items.some(inFlight)
  if (shouldGuard === guardingUnload) return
  guardingUnload = shouldGuard
  if (shouldGuard) window.addEventListener('beforeunload', warnBeforeUnload)
  else window.removeEventListener('beforeunload', warnBeforeUnload)
}

function commit(next: PhotoUploadItem[]) {
  items = next
  syncUnloadGuard()
  listeners.forEach((listener) => listener())
}

export const photoUploadStore = {
  subscribe(listener: Listener): () => void {
    listeners.add(listener)
    return () => listeners.delete(listener)
  },
  getItems: (): PhotoUploadItem[] => items,
  find: (key: string): PhotoUploadItem | undefined =>
    items.find((item) => item.key === key),
  add(newItems: PhotoUploadItem[]) {
    commit([...items, ...newItems])
  },
  patch(key: string, patch: Partial<PhotoUploadItem>) {
    commit(
      items.map((item) => (item.key === key ? { ...item, ...patch } : item))
    )
  },
  remove(key: string) {
    const removed = items.find((item) => item.key === key)
    if (removed) URL.revokeObjectURL(removed.previewUrl)
    commit(items.filter((item) => item.key !== key))
  },
  reset() {
    items.forEach((item) => URL.revokeObjectURL(item.previewUrl))
    commit([])
  },
}
